import React, { useState, useEffect, useMemo, useCallback } from "react";
import { ColumnType } from "build-ts/enums";
import { KGColumnProto } from "build-ts/knowledege_graph";
import { getKGColumnById, updateKGColumn } from "services/DataModelService";
import { listScheduledJobsByOwnerId, setScheduledJobIsActive } from "services/ScheduleServices";
import { ScheduledJobProto, ScheduledJobType } from "build-ts/schedule";
import { useConfigurationContext } from "context/ConfigurationContext";
import showToast from "hooks/useCustomToast";
import KGColumnEditForm from "../components/KGColumnEditForm";

interface KGColumnEditFormContainerProps {
  id: string | undefined;
  onClose: () => void;
  onSave?: () => void;
  triggerSubmit?: boolean;
  setTriggerSubmit?: (value: boolean) => void;
  onReloadKnowledgeGraph?: () => void;
  compact?: boolean;
}

const KGColumnEditFormContainer: React.FC<KGColumnEditFormContainerProps> = ({
  id,
  onClose,
  onSave,
  triggerSubmit,
  setTriggerSubmit,
  onReloadKnowledgeGraph,
  compact,
}) => {
  const { modelList, fetchModelList } = useConfigurationContext();

  const [selectedColumn, setSelectedColumn] = useState<KGColumnProto | null>(null);
  const [columnType, setColumnType] = useState<ColumnType>(ColumnType.ATTRIBUTE);
  /** Backend: 1 = low cardinality, 0 = high cardinality */
  const [cardinality, setCardinality] = useState<number>(0);
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Map of model id → model name, derived from the context model list
  const modelNameById = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const h of modelList) {
      if (h?.id) map[h.id] = h.name?.trim() || h.id;
    }
    return map;
  }, [modelList]);

  const loadColumn = useCallback(async (columnId: string) => {
    if (!columnId) {
      setSelectedColumn(null);
      return;
    }
    setIsLoading(true);
    const kgColumn = await getKGColumnById(columnId, true);
    if (kgColumn) {
      setSelectedColumn(kgColumn);
      setName(kgColumn.header?.name ?? "");
      setDescription(kgColumn.header?.description ?? "");
      setColumnType(kgColumn.column_type ?? ColumnType.ATTRIBUTE);
      setCardinality(kgColumn.cardinality === 1 ? 1 : 0);
    }
    setIsLoading(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedColumn) return;
    if (!name.trim()) {
      showToast("Name is required", "", "error");
      return;
    }

    const updatedKgColumn: KGColumnProto = {
      ...selectedColumn,
      header: { ...selectedColumn.header, name, description },
      column_type: columnType,
    };

    if (columnType === ColumnType.ATTRIBUTE) {
      updatedKgColumn.cardinality = cardinality === 1 ? 1 : 0;
      delete updatedKgColumn.aggregation;
      delete updatedKgColumn.time_bucket;
    } else if (columnType === ColumnType.MEASURE) {
      // Aggregation not edited in UI; keep existing value from selectedColumn via spread.
      delete updatedKgColumn.cardinality;
      delete updatedKgColumn.time_bucket;
    } else if (columnType === ColumnType.TEMPORAL) {
      delete updatedKgColumn.aggregation;
      delete updatedKgColumn.cardinality;
      // Time bucket not edited in UI; keep existing value from selectedColumn via spread.
    }

    const result = await updateKGColumn(updatedKgColumn, "", [], [], false);
    if (result) {
      showToast("KG Column updated successfully", "", "success");
      // When cardinality flips from low → high, deactivate the sync job since syncing
      // is no longer applicable. Activation when going high → low is left to the user.
      const originalCardinality = selectedColumn.cardinality === 1 ? 1 : 0;
      if (
        columnType === ColumnType.ATTRIBUTE &&
        originalCardinality === 1 &&
        cardinality === 0 &&
        selectedColumn.header?.id
      ) {
        const jobs = await listScheduledJobsByOwnerId(selectedColumn.header.id);
        const syncJob = jobs?.find((j: ScheduledJobProto) => j.type === ScheduledJobType.COLUMN_SYNC_JOB);
        if (syncJob?.header?.id) {
          await setScheduledJobIsActive(syncJob.header.id, false);
        }
      }
      onSave?.();
      onClose();
      onReloadKnowledgeGraph?.();
    }
  }, [selectedColumn, name, description, columnType, cardinality, onSave, onClose, onReloadKnowledgeGraph]);

  // Load column whenever the id changes
  useEffect(() => {
    if (id) loadColumn(id);
  }, [id, loadColumn]);

  // Ensure the model list is populated for source-column display
  useEffect(() => {
    if (modelList.length === 0) fetchModelList();
  }, []);

  // Allow parent components to trigger a save via the triggerSubmit prop
  useEffect(() => {
    if (triggerSubmit) {
      handleSave();
      setTriggerSubmit?.(false);
    }
  }, [triggerSubmit]);

  return (
    <KGColumnEditForm
      id={id}
      isLoading={isLoading}
      selectedColumn={selectedColumn}
      name={name}
      description={description}
      cardinality={cardinality}
      columnType={columnType}
      modelNameById={modelNameById}
      compact={compact}
      onNameChange={setName}
      onDescriptionChange={setDescription}
      onCardinalityChange={setCardinality}
    />
  );
};

export default KGColumnEditFormContainer;
